/**
 * GP-1338 — is an order's `completed` status still TRUE right now?
 *
 * `status` and `workflowStage` are two independent columns and nothing keeps them
 * in step once an order moves BACKWARDS. `completeDelivery` (and the automated
 * `Ready to Ship → Delivered` job) stamps `status = 'completed'`, but pulling the
 * order back into troubleshooting, rescheduling it and cancelling its shipment
 * only rewrites `workflowStage` / `inTroubleshooting` — the terminal `status`
 * sticks. ORD-20260709-135539-375 was left at `status='completed'` +
 * `workflowStage='clinic_scheduling'` + `inTroubleshooting=true`, so the patient
 * Order History grid painted a green **Completed** chip and the order detail
 * header painted **100% Complete** with all eight pipeline nodes green, while the
 * Scheduling Queue → Troubleshooting tab listed the very same order as open work.
 *
 * Every surface that renders a completion verdict derives it here, so the grid,
 * the detail header and the pipeline can never disagree with each other again.
 */

/** Minimal order shape needed to judge completion. Both the patient Order History
 *  row (`any` from `/api/orders/:pharmacyId`) and the order detail response fit. */
export interface OrderCompletionInput {
  status?: string | null;
  workflowStage?: string | null;
  inTroubleshooting?: boolean | null;
}

const normalize = (value?: string | null): string =>
  String(value ?? '').trim().toLowerCase();

/**
 * Stages an order can legitimately REST on. Everything else is a stage staff are
 * still expected to work, which is what makes a `completed` status a contradiction.
 * `cancelled` counts as terminal: a cancelled order is closed, not reopened.
 */
const TERMINAL_WORKFLOW_STAGES = new Set(['delivered', 'picked_up', 'cancelled']);

export const isTerminalWorkflowStage = (stage?: string | null): boolean =>
  TERMINAL_WORKFLOW_STAGES.has(normalize(stage));

export interface OrderCompletionState {
  /** What the row claims: `status === 'completed'`. */
  claimsCompleted: boolean;
  /**
   * Completed, but parked on a stage staff still work — the order came BACK into
   * the workflow after being closed and the stored status is stale. Surfaces must
   * show the live stage, not the terminal chip / all-green pipeline.
   */
  isReopenedAfterCompletion: boolean;
  /**
   * Completed AND still flagged `inTroubleshooting`, so the troubleshooting queues
   * (which filter on the flag alone, never on `status`) still list it as open work.
   * NOT the same as reopened: an order can be missed, sent to troubleshooting and
   * then genuinely delivered with the flag never cleared — the delivery is real,
   * but the queue row is real too and the grid must not read as "nothing to do".
   */
  hasOpenTroubleshooting: boolean;
  /** Safe to paint the terminal treatment: all-green pipeline, 100%, green chip. */
  isGenuinelyClosed: boolean;
}

export const deriveOrderCompletionState = (
  order?: OrderCompletionInput | null,
): OrderCompletionState => {
  const claimsCompleted = normalize(order?.status) === 'completed';
  const stage = normalize(order?.workflowStage);

  // No stage recorded → nothing to contradict the status; treat it as closed.
  const isReopenedAfterCompletion =
    claimsCompleted && stage !== '' && !isTerminalWorkflowStage(stage);

  return {
    claimsCompleted,
    isReopenedAfterCompletion,
    hasOpenTroubleshooting: claimsCompleted && !!order?.inTroubleshooting,
    isGenuinelyClosed: claimsCompleted && !isReopenedAfterCompletion,
  };
};
